import React, { FC, useMemo, useRef } from 'react'
import { BUTTON_SIZE, BUTTON_TYPE, styled } from '~/app/components'
import { LeisureProductDetailInfo } from '~/__generated__/__types__'
import LeisureDetailInfo from '~/app/components/common/Leisure/LeisureDetailInfo'
import Button from '../Button/Button'
import BottomSheet from '~/app/components/common/BottomSheet/BottomSheet'
import LeisureCancelPolicyText from './LeisureCancelPolicyText'
import { CancellationFeePlan } from './types'
import { isEmpty } from 'ramda'
import { findPlanOrLast } from '../../order-converter/converter/LeisureOrderConverter'
import useModalHash from '~/app/components/common/useModalHash'

const LeisureCancelPolicyContainer = styled.div`
  text-align: right;
`

const RefundInfoContainer = styled.div`
  padding: 0.8rem 2rem 0;
`

interface ILeisureCancelPolicy {
  refundInfo?: LeisureProductDetailInfo[]
  /**
   * @defaultValue true
   */
  isAvailableUse?: boolean
  cancellationFeePlans: CancellationFeePlan[]
}

/**
 * @summary 레저 > 상품 정보 > 취소 정책 조회
 * {@link https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/90623336/02.} - AC
 * {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103745201/FES+Component} - spec 문서
 * @test app/components/common/LeisureCancelPolicy.stories.tsx
 *
 * @param refundInfo - 취소/환불 규정 (from Product)
 * @param isAvailableUse - 이용 가능한 상태 여부. 사용 가능한 상태의 상품만 취소 정책 노출.
 * @param cancellationFeePlans - 취소 수수료 정책 (from Variant)
 */
const LeisureCancelPolicy: FC<ILeisureCancelPolicy> = ({
  refundInfo,
  isAvailableUse = true,
  cancellationFeePlans,
}) => {
  const cancellationFeePlan = findPlanOrLast(cancellationFeePlans)
  const [isRefundInfoOpen, setIsRefundInfoOpen] = useModalHash({ id: 'leisure-cancel-policy' })
  const hasRefundInfo = useMemo(() => !!refundInfo && !!refundInfo.length, [refundInfo])
  const refundInfoBottomSheet = useRef(
    <Button
      key={'leisure-cancel-policy'}
      size={BUTTON_SIZE.L}
      type={BUTTON_TYPE.BASIC}
      title={'확인'}
      onClick={() => setIsRefundInfoOpen(false)}
      isFullSize
    />
  ).current

  if (!isAvailableUse || isEmpty(cancellationFeePlans)) {
    return null
  }

  const handleClickRefundablePolicy = () => {
    if (hasRefundInfo) {
      setIsRefundInfoOpen(true)
    }
  }

  return (
    // React Portal 로 등록된 컴포넌트도 선언된 곳에서부터 이벤트 전이가 발생하기 때문에
    // 이를 막기 위해 stopPropagation 을 사용
    <LeisureCancelPolicyContainer onClick={e => e.stopPropagation()}>
      <LeisureCancelPolicyText
        cancellationFeePlan={cancellationFeePlan}
        hasRefundInfo={hasRefundInfo}
        onClickRefundablePolicy={handleClickRefundablePolicy}
      />
      {isRefundInfoOpen && (
        <BottomSheet
          isOpened
          title={'취소규정'}
          onRequestClose={() => setIsRefundInfoOpen(false)}
          buttons={[refundInfoBottomSheet]}
        >
          <RefundInfoContainer>
            <LeisureDetailInfo infos={refundInfo ?? []} level={0} />
          </RefundInfoContainer>
        </BottomSheet>
      )}
    </LeisureCancelPolicyContainer>
  )
}

export default LeisureCancelPolicy
